import { agentService, CreateAgentRequest } from '../services/api'

export interface JCMDCommand {
  name: string
  description: string
  handler: (description?: string,content?: string) => Promise<any> | void
  supported: boolean
}

export interface JCMDExecution {
  id: string
  command: string
  description?: string
  commandData?: string
  status: 'pending' | 'executing' | 'completed' | 'failed'
  result?: any
  error?: string
  timestamp: number
}

class JCMDRegistry {
  private commands: Map<string, JCMDCommand> = new Map()
  private executions: Map<string, JCMDExecution> = new Map()
  private executionQueue: string[] = []
  private isProcessing = false

  constructor() {
    // 注册默认指令
    this.registerDefaultCommands()
  }

  // 注册指令
  register(command: JCMDCommand) {
    this.commands.set(command.name.toUpperCase(), command)
    console.log(`JCMD Command registered: ${command.name}`)
  }

  // 获取指令
  getCommand(name: string): JCMDCommand | undefined {
    const command = this.commands.get(name.toUpperCase())
    if (command) {
      return command
    }
    
    // 为未知指令返回默认描述
    return {
      name: name.toUpperCase(),
      description: `未知指令: ${name}`,
      handler: () => Promise.resolve(),
      supported: false
    }
  }

  // 获取所有指令
  getAllCommands(): JCMDCommand[] {
    return Array.from(this.commands.values())
  }

  // 检查指令是否支持
  isSupported(name: string): boolean {
    const command = this.getCommand(name)
    return command?.supported || false
  }

  // 执行指令
  async execute(command: string, description?: string, commandData?: string): Promise<JCMDExecution> {
    const executionId = `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    const execution: JCMDExecution = {
      id: executionId,
      command,
      description,
      commandData,
      status: 'pending',
      timestamp: Date.now()
    }

    this.executions.set(executionId, execution)
    this.executionQueue.push(executionId)

    console.log(`JCMD Execution queued: ${command}`, execution)

    // 开始处理队列
    this.processQueue()

    return execution
  }

  // 处理执行队列
  private async processQueue() {
    if (this.isProcessing || this.executionQueue.length === 0) {
      return
    }

    this.isProcessing = true

    while (this.executionQueue.length > 0) {
      const executionId = this.executionQueue.shift()!
      const execution = this.executions.get(executionId)

      if (!execution) continue
      try {
        execution.status = 'executing'
        console.log(`JCMD Executing: ${execution.command}`)

        const command = this.getCommand(execution.command)
        if (command) {
          const result = await command.handler(execution.description,execution.commandData)
          execution.status = 'completed'
          execution.result = result
          console.log(`JCMD Completed: ${execution.command}`, result)
        } else {
          execution.status = 'failed'
          execution.error = `Command not found: ${execution.command}`
          console.warn(`JCMD Failed: ${execution.command} - Command not found`)
        }
      } catch (error) {
        let errorMessage: string
        try {
          // 尝试从错误对象中提取消息
          if (error && typeof error === 'object') {
            const errorObj = error as any
            if (errorObj.details?.data?.message) {
              errorMessage = String(errorObj.details.data.message)
            } else if (errorObj.message) {
              errorMessage = String(errorObj.message)
            } else if (errorObj.toString) {
              errorMessage = errorObj.toString()
            } else {
              errorMessage = JSON.stringify(error)
            }
          } else {
            errorMessage = String(error)
          }
        } catch (stringifyError) {
          // 如果转换失败，使用默认错误信息
          errorMessage = '执行过程中发生未知错误'
        }
        
        execution.status = 'failed'
        execution.error = errorMessage
        console.error(`JCMD Failed: ${execution.command}`, errorMessage)
      }
    }

    this.isProcessing = false
  }

  // 获取执行状态
  getExecution(id: string): JCMDExecution | undefined {
    return this.executions.get(id)
  }

  // 获取所有执行记录
  getAllExecutions(): JCMDExecution[] {
    return Array.from(this.executions.values()).sort((a, b) => b.timestamp - a.timestamp)
  }

  // 注册默认指令
  private registerDefaultCommands() {
    // ALERT 指令
    this.register({
      name: 'ALERT',
      description: '显示提示框',
      supported: true,
      handler: (params: any) => {
        console.log('ALERT command executed:', params)
        // 这里可以集成实际的提示框显示逻辑
        return Promise.resolve()
      }
    })

    // CREATE 指令
    this.register({
      name: 'CREATE',
      description: '创建 AI Agent',
      supported: true,
      handler: async (description?: string, content?: string) => {
        console.log('CREATE command executed with content:', content)
        
        try {
          // 检查用户是否已登录
          const token = localStorage.getItem("token")
          if (!token) {
            throw new Error('用户未登录，请先登录')
          }
          // 解析内容中的参数
          let agentData: any = {}
          if (content) {
            try {
              // 尝试解析JSON格式的内容
              agentData = JSON.parse(content)
            } catch (e) {
              // 如果不是JSON格式，将内容作为描述
              agentData = {
                name: 'AI Agent',
                description: content,
                type: 'conversation'
              }
            }
          }

          // 确保必要的字段存在，严格按照CreateAgentRequest接口
          const sessionId = localStorage.getItem('agent_session_id') || agentData.sessionId
          if (!sessionId) throw new Error('缺少 sessionId，无法创建Agent')
          const createData: CreateAgentRequest = {
            agentName: agentData.agentName || 'AI Agent',
            description: description || 'AI Agent created via JCMD',
            agentType: agentData.agentType,
            modelId: agentData.modelId || agentData.model || undefined,
            prompt: agentData.prompt || undefined,
            tags: Array.isArray(agentData.tags) ? agentData.tags : [],
            config: agentData.config || {},
            sessionId // 新增
          }

          // 验证必需字段
          if (!createData.agentName.trim()) {
            throw new Error('Agent名称不能为空')
          }
          if (!createData.description.trim()) {
            throw new Error('Agent描述不能为空')
          }


          // 调用后端API创建Agent
          const newAgent = await agentService.createAgent(createData)
          
          // 创建成功后，将agentId存储到localStorage，让聊天页面检测到并显示成功模块
          if (newAgent && newAgent.id) {
            // 将agentId存储到localStorage，聊天页面会检测这个值来显示成功模块
            localStorage.setItem('created_agent_id', newAgent.id)
            
            return {
              success: true,
              agent: newAgent,
              message: `Agent "${newAgent.name}" 创建成功。`
            }
          } else {
            throw new Error('创建Agent成功但未获取到ID')
          }
        } catch (error) {
          console.error('Failed to create agent:', error)
          throw error
        }
      }
    })

    // BUTTON 指令
    this.register({
      name: 'BUTTON',
      description: '创建按钮',
      supported: false,
      handler: (params: any) => {
        console.log('BUTTON command executed:', params)
        return Promise.resolve()
      }
    })

    // MODAL 指令
    this.register({
      name: 'MODAL',
      description: '显示弹窗',
      supported: false,
      handler: (params: any) => {
        console.log('MODAL command executed:', params)
        return Promise.resolve()
      }
    })
  }
}

// 创建全局实例
export const jcmdRegistry = new JCMDRegistry() 